/*
 * Copyright (c) 2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { Permission, Role, RoleInfo } from '@cp/ic-sdk'

const RESOURCE_ORG = 'ORG'
const RESOURCE_PROJECT = 'PROJ'

export class PermissionHelpers {
  static hasSufficientPermission({
    requiredPermission,
    permissionResources = {},
    isSuperAdmin,
    userRoles,
    rolesRecord
  }: {
    requiredPermission: Permission
    permissionResources?: Record<string, string>
    isSuperAdmin: boolean

    userRoles: Role[]
    rolesRecord: Record<string, RoleInfo>
  }): boolean {
    if (isSuperAdmin) {
      return true
    }

    const effectiveRequirePermission = { ...requiredPermission }
    for (const key in permissionResources) {
      effectiveRequirePermission.resource = effectiveRequirePermission.resource.replace(`{${key}}`, permissionResources[key])
    }

    for (const userRole of userRoles) {
      const rolePermissions = rolesRecord[userRole.roleId]?.permissions
      if (!rolePermissions) {
        return false
      }

      const ownedPermissions = replacePermissionResourceValues(rolePermissions, userRole.organizationId ?? '', userRole.projectId ?? '')
      if (isPermissionAllowed(ownedPermissions, effectiveRequirePermission)) {
        return true
      }
    }

    return false
  }

  static isPermissionAllowed = isPermissionAllowed
}

function isPermissionAllowed(grantedPermissions: Permission[], requiredPermission: Permission) {
  for (const grantedPermission of grantedPermissions) {
    const grantedAction = grantedPermission.action
    if (
      isResourceAllowed(grantedPermission.resource, requiredPermission.resource) &&
      isActionAllowed(grantedAction, requiredPermission.action)
    ) {
      return true
    }
  }

  return false
}

function isResourceAllowed(currentPermissionResource: string, requiredPermissionResource: string) {
  const requiredPermissionSegments = requiredPermissionResource.split(':')
  const requiredPermissionsLength = requiredPermissionSegments.length

  const currentPermissionSegments = currentPermissionResource.split(':')
  const currentPermissionsLength = currentPermissionSegments.length

  let minimumSectionLength = currentPermissionsLength
  if (minimumSectionLength > requiredPermissionsLength) {
    minimumSectionLength = requiredPermissionsLength
  }

  // Check 1: check if each segment is strictly equal.
  for (let i = 0; i < minimumSectionLength; i++) {
    const currentSegment = currentPermissionSegments[i]
    const requiredSegment = requiredPermissionSegments[i]

    if (currentSegment !== requiredSegment && currentSegment !== '*') {
      return false
    }
  }

  // Check 2: each segment is not strictly equal (there may be `*`); check their length.
  if (currentPermissionsLength === requiredPermissionsLength) {
    return true
  }

  // Check 3: check if the last segment of the current user's permission is "*" and this "*" replaces placeholder
  // of either {organizationId} or {projectId}.
  if (currentPermissionsLength < requiredPermissionsLength) {
    if (currentPermissionSegments[currentPermissionsLength - 1] === '*') {
      if (currentPermissionsLength < 2) {
        return true
      }

      const segment = currentPermissionSegments[currentPermissionsLength - 2]
      if (segment === RESOURCE_ORG || segment === RESOURCE_PROJECT) {
        return false
      }

      return true
    }

    return false
  }

  // Check 4: if the required permissions is shorter than current permissions,
  // then check if the current permission segment addresses all resources with `*`.
  for (let i = requiredPermissionsLength; i < currentPermissionsLength; i++) {
    if (currentPermissionSegments[i] !== '*') {
      return false
    }
  }

  return true
}

function isActionAllowed(grantedAction: number, requiredAction: number) {
  return (grantedAction & requiredAction) === requiredAction
}

function replacePermissionResourceValues(grantedPermissions: Permission[], allowedOrganizationID: string, allowedProjectID: string) {
  const newGrantedPermissions = [...grantedPermissions]

  for (const newGrantedPermission of newGrantedPermissions) {
    if (allowedOrganizationID.length > 0) {
      newGrantedPermission.resource = newGrantedPermission.resource.replace('{organizationId}', allowedOrganizationID)
    }
    if (allowedProjectID.length > 0) {
      newGrantedPermission.resource = newGrantedPermission.resource.replace('{projectId}', allowedProjectID)
    }
  }

  return newGrantedPermissions
}
